import { i18n } from '../../i18n/index.js';
import { formatDate as baseDate, formatMoney as baseMoney } from './index.js';

// TODO: use the workspace timezone when it is exposed to the frontend scope.
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function formatDateTime(value?: string | Date | null, timeZone?: string): string {
  if (!value || !Number.isFinite(new Date(value).getTime())) return '—';
  const text = new Intl.DateTimeFormat(i18n.locale === 'en' ? 'en-GB' : 'ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(value));
  if (text === '—' || !timeZone || timeZone === browserTimeZone()) return text;
  const zone = new Intl.DateTimeFormat(i18n.locale === 'en' ? 'en-US' : 'ru-RU', {
    timeZone,
    timeZoneName: 'short',
  })
    .formatToParts(new Date(value!))
    .find((part) => part.type === 'timeZoneName')?.value;
  return zone ? `${text} ${zone}` : text;
}

export function formatDate(value?: string | Date | null): string {
  return baseDate(value, { readable: true });
}

export function formatTime(value: string | Date): string {
  return new Intl.DateTimeFormat(i18n.locale === 'en' ? 'en-US' : 'ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(value));
}

export function formatMoney(value: number | string | null | undefined, currency: string): string {
  return baseMoney(value, currency, 'symbol');
}

export function formatMeetingRange(startsAt: string, endsAt: string): string {
  return `${formatDateTime(startsAt)}–${dayKey(startsAt) === dayKey(endsAt) ? formatTime(endsAt) : formatDateTime(endsAt)}`;
}

export function formatRelative(value?: string | Date | null, now = new Date()): string {
  if (!value) return '—';
  const delta = new Date(value).getTime() - now.getTime();
  if (!Number.isFinite(delta)) return '—';
  const abs = Math.abs(delta);
  const [unit, divisor]: [Intl.RelativeTimeFormatUnit, number] =
    abs < 60_000
      ? ['second', 1000]
      : abs < 3_600_000
        ? ['minute', 60_000]
        : abs < 86_400_000
          ? ['hour', 3_600_000]
          : ['day', 86_400_000];
  return new Intl.RelativeTimeFormat(i18n.locale === 'en' ? 'en-US' : 'ru-RU', {
    numeric: 'auto',
  }).format(Math.round(delta / divisor), unit);
}

export function formatRelativeShort(value?: string | Date | null, now = new Date()): string {
  if (!value) return '—';
  const delta = new Date(value).getTime() - now.getTime();
  if (!Number.isFinite(delta)) return '—';
  const abs = Math.abs(delta);
  if (abs > 365 * 86_400_000) return formatDateTime(value).slice(0, 10);
  if (abs < 60_000) return i18n.t('crm.entity_detail.relative_short.now');
  const [unit, divisor] =
    abs < 3_600_000
      ? ['minute', 60_000]
      : abs < 86_400_000
        ? ['hour', 3_600_000]
        : ['day', 86_400_000];
  return i18n.t(`crm.entity_detail.relative_short.${unit}_${delta > 0 ? 'future' : 'past'}`, {
    count: Math.floor(abs / divisor),
  });
}

export function dayKey(value: string | Date): string {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function dayLabel(value: string, now = new Date()): string {
  if (dayKey(value) === dayKey(now)) return i18n.t('crm.entity_detail.today');
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  return dayKey(value) === dayKey(yesterday)
    ? i18n.t('crm.entity_detail.yesterday')
    : formatDate(value);
}

export function taskDueState(
  dueAt: string | null | undefined,
  now = new Date(),
): 'overdue' | 'today' | 'future' | 'none' {
  if (!dueAt || !Number.isFinite(new Date(dueAt).getTime())) return 'none';
  if (new Date(dueAt).getTime() < now.getTime()) return 'overdue';
  return dayKey(dueAt) === dayKey(now) ? 'today' : 'future';
}
